import { slugs } from '@/constants/slugs'
import { ContentEpisodeEdge } from '@/types/codegen-federation'
import { isARecap } from '@/utils/EpisodeUtil'
import { Episode, Project, Season } from './types'

/** Shape of the new content API season node */
interface TitleSeasonNode {
  id: string
  name?: string
  seasonNumber?: number
  episodes?: { edges?: ContentEpisodeEdge[] }
}

/** Shape of the new content API episode node we care about */
interface TitleEpisodeNode {
  id: string
  name?: string
  description?: string
  episodeNumber?: number
  posterCloudinaryPath?: string
  posterLandscapeCloudinaryPath?: string
  slug?: string
  subtitle?: string
  duration?: number
  earlyAccessDate?: string
  publiclyAvailableDate?: string
  guildAvailableDate?: string
  isTrailer?: boolean
  unavailableReason?: Episode['unavailableReason']
  watchPosition?: Episode['watchPosition']
}

function mapEpisodeNode(epNode: TitleEpisodeNode, season: TitleSeasonNode, project: Project): Episode {
  const duration = epNode.duration ?? 0
  const episodeNumber = epNode.episodeNumber ?? 0
  const base: Episode = {
    description: epNode.description ?? '',
    earlyAccessDate: epNode.earlyAccessDate,
    episodeNumber,
    guid: epNode.id,
    guildAvailableDate: epNode.guildAvailableDate,
    id: epNode.id,
    isTrailer: epNode.isTrailer ?? false,
    name: epNode.name ?? '',
    posterCloudinaryPath: epNode.posterCloudinaryPath ?? '',
    posterLandscapeCloudinaryPath: epNode.posterLandscapeCloudinaryPath,
    projectSlug: project.slug,
    publiclyAvailableDate: epNode.publiclyAvailableDate,
    releaseDate: epNode.publiclyAvailableDate ?? '',
    seasonId: season.id,
    seasonNumber: season.seasonNumber ?? 0,
    slug: epNode.slug ?? '',
    source: { duration, url: '' },
    subtitle: epNode.subtitle ?? '',
    unavailableReason: epNode.unavailableReason,
    watchPosition: epNode.watchPosition,
  }

  if ((!base.name || base.name.trim() === '') && base.subtitle) {
    base.name = base.subtitle
  }
  if (project.projectType === 'series' && episodeNumber > 0 && !isARecap({ name: epNode.name ?? '' })) {
    base.name = `Episode ${episodeNumber}`
  }
  if (isARecap({ name: epNode.name ?? '' }) && (!base.subtitle || base.subtitle.trim() === '')) {
    base.subtitle = base.name
  }
  return base
}

export function normalizeProjectSeasons(project: Project | undefined): Project | undefined {
  if (!project) return project
  if (project.slug === slugs.dryBar) return project
  const titleSeasonsEdges = project.title?.seasons?.edges
  // If the new content API doesn't provide seasons under project.title,
  // for non-series titles we fall back to project.seasons
  if (!titleSeasonsEdges || titleSeasonsEdges.length === 0) {
    return project
  }

  const mappedSeasons: Season[] = titleSeasonsEdges
    .map((edge) => edge?.node as TitleSeasonNode | undefined)
    .filter((node): node is TitleSeasonNode => Boolean(node))
    .map((seasonNode) => {
      const episodes: Episode[] = (seasonNode.episodes?.edges || [])
        .map((epEdge) => epEdge?.node as TitleEpisodeNode | undefined)
        .filter((ep): ep is TitleEpisodeNode => Boolean(ep))
        .map((epNode) => mapEpisodeNode(epNode, seasonNode, project))

      return {
        id: seasonNode.id,
        name: seasonNode.name ?? '',
        seasonNumber: seasonNode.seasonNumber,
        episodes,
      } as Season
    })

  return { ...project, seasons: mappedSeasons }
}
